import React from "react";
import { Link } from "react-router-dom";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { useUnit } from "effector-react";
import { $sectorsTerms } from "@/core/store/terms";
import { Idea } from "../model/types";
import "./IdeaGridItem.scss";
import { imageList } from "@/core/utils/imageList";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { Tags } from "@/core/components/tags/Tags";
import { UserIdea } from "@/features/users/model/types";

interface IdeaGridItemProps {
  idea: UserIdea;
}

export const UserIdeaGridItem: React.FC<IdeaGridItemProps> = ({ idea }) => {
  // Get sectors terms from store
  const sectorsTerms = useUnit($sectorsTerms);

  // Helper function to check if a value is a numeric string (ID)
  const isNumericString = (value: string): boolean => {
    return /^\d+$/.test(value);
  };

  // Helper function to get sector name from ID using terms store
  const getSectorNameFromId = (
    sectorValue: string | { name: string; id: string },
  ): string => {
    // If it's an object with name property, return the name
    if (typeof sectorValue === "object" && sectorValue?.name) {
      return sectorValue.name;
    }

    // If it's a string
    if (typeof sectorValue === "string") {
      // If it's not a numeric string, return as-is (it's already a name)
      if (!isNumericString(sectorValue)) {
        return sectorValue;
      }

      // It's a numeric string, so look up the name in terms store
      if (sectorsTerms.data && sectorsTerms.data.length > 0) {
        const sector = sectorsTerms.data.find(
          (term) => term.tid === sectorValue,
        );
        return sector ? sector.name : sectorValue;
      }
    }

    return String(sectorValue); // Fallback
  };
  return (
    <div className="idea-grid-item eu-card">
      {/* <div className="card-label">
        <span className="label-text">
          {idea.status} {idea.status === 'Draft' && ` - ${idea.state}`}
        </span>
      </div> */}
      <div className="idea-badges">
        {idea?.is_owner && <div className="idea-badge">Owner</div>}
        {idea?.is_member && !idea?.is_owner && (
          <div className="idea-badge">Guest</div>
        )}
        {idea?.is_coordinator && !idea?.is_owner && (
          <div className="idea-badge">Coordinator</div>
        )}
        {idea.state && idea.state.toLowerCase() !== "approved" && (
          <div className="idea-badge">{idea.state}</div>
        )}
      </div>

      <div className="card-content">
        <div className="card-header">
          <h3 className="card-title">
            <Link to={`/ideas/${idea.id}`}>
              {typeof idea.title === "string" ? idea.title : String(idea.title)}
            </Link>
          </h3>
        </div>

        <div className="card-description">
          <p>
            {idea.description
              ? typeof idea.description === "string"
                ? idea.description.length > 200
                  ? `${idea.description.substring(0, 200)}...`
                  : idea.description
                : String(idea.description)
              : "No description available"}
          </p>
        </div>

        <div className="card-categories">
          {idea.sectors && idea.sectors.length > 2 ? (
            <>
              {idea.sectors.slice(0, 4).map((sector, index) => {
                const sectorName = getSectorNameFromId(sector);
                return (
                  <span
                    key={`sector-cat-${idea.id}-${index}`}
                    className="category"
                  >
                    {sectorName}
                  </span>
                );
              })}
              {idea.sectors.length > 4 && (
                <span className="count-badge">+{idea.sectors.length - 4}</span>
              )}
            </>
          ) : idea.eu_expertise && idea.eu_expertise.length > 0 ? (
            idea.eu_expertise.slice(0, 2).map((expertise, index) => {
              const expertiseName =
                typeof expertise === "string" ? expertise : String(expertise);
              return (
                <span
                  key={`expertise-${idea.id}-${index}`}
                  className="category"
                >
                  {expertiseName}
                </span>
              );
            })
          ) : (
            <span className="category">General Interest</span>
          )}
        </div>
      </div>

      <div className="card-footer">
        <div className="partner-section">
          <div className="partner-avatars">
            <div className="eu-flag">
              <img
                src={
                  idea.programmes && idea.programmes.length > 0
                    ? idea.programmes[0].logo
                    : imageList.noimage
                }
                alt="EU call"
              />
            </div>
          </div>
          <span className="call-type">
            {idea.programmes &&
              idea.programmes.length > 0 &&
              idea.programmes[0].name}
          </span>
        </div>
        <div className="idea-meta">
          <div className="partnership-section">
            <span className="label">Partnership</span>
            <div className="partnership-logos ">
              {idea.is_member || idea.is_owner ? (
                idea.partnership &&
                (Array.isArray(idea.partnership)
                  ? idea.partnership.length > 0
                  : Object.keys(idea.partnership).length > 0) && (
                  <div className="interested-partners">
                    <div className="partners-avatars">
                      {Object.values(idea?.partnership)
                        ?.slice(0, 4)
                        .map((p, i) => (
                          <div
                            key={p.id || i}
                            className="partner-avatar-wrapper"
                            style={{
                              transform:
                                i > 0 ? `translateX(-${30 * i}px)` : "none",
                              zIndex: i > 0 ? 2 + (i - 1) : 1,
                            }}
                          >
                            <div className="company-logo placeholder">
                              <BgImage
                                img={p.logo_uri}
                                altImage={imageList.noOrgimage}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )
              ) : (
                <div>
                  {idea.partnership && Array.isArray(idea.partnership) && (
                    <Tags type="partner" number={idea.partnership_count} />
                  )}
                </div>
              )}
              {idea.partnership && Array.isArray(idea.partnership)
                ? idea.partnership.length > 0
                : Object.keys(idea.partnership).length > 0 &&
                  Object.keys(idea.partnership).length > 4 && (
                    <span className="more-partners">
                      +{Object.keys(idea.partnership).length - 4}
                    </span>
                  )}
            </div>
          </div>

          <div className="looking-for-section">
            <span className="label">Looking for</span>
            {idea.looking_for && idea.looking_for.length > 0 ? (
              <span className="looking-for-text">
                {[idea.looking_for?.map((item: any) => item.name) || []]
                  .filter(Boolean)
                  .join(",")}
              </span>
            ) : (
              <span className="looking-for-text">-</span>
            )}
          </div>
        </div>
        {/* <div className="partnership-info">
          <div className="info-row">
            <span className="info-label">Partnership</span>
            <span className="info-value">
              {idea.partnership &&
              (Array.isArray(idea.partnership) ? idea.partnership.length > 0 : Object.keys(idea.partnership).length > 0)
                ? 'Available'
                : 'Looking for'}
            </span>
          </div>
          <div className="partner-logos">
            {(() => {
              const hasPartnership =
                idea.partnership &&
                (Array.isArray(idea.partnership)
                  ? idea.partnership.length > 0
                  : Object.keys(idea.partnership).length > 0);

              if (hasPartnership) {
                const firstPartnershipEntry = Array.isArray(idea.partnership)
                  ? idea.partnership[0]
                  : Object.values(idea.partnership)[0];

                // Extract the actual partner from PartnershipEntry or use direct Partnership
                const firstPartner =
                  firstPartnershipEntry && 'partner' in firstPartnershipEntry
                    ? firstPartnershipEntry.partner
                    : firstPartnershipEntry;

                const partnershipCount = Array.isArray(idea.partnership)
                  ? idea.partnership.length
                  : Object.keys(idea.partnership).length;

                return (
                  <>
                    <div className="partner-logo">
                      {firstPartner?.logo_uri && firstPartner.logo_uri !== 'partner-images/logo_not_available.jpg' ? (
                        <img
                          src={`/${firstPartner.logo_uri}`}
                          alt={firstPartner.title}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="placeholder"
                        style={{
                          display:
                            firstPartner?.logo_uri && firstPartner.logo_uri !== 'partner-images/logo_not_available.jpg'
                              ? 'none'
                              : 'flex',
                        }}
                      >
                        <span>{firstPartner?.title?.[0] || '?'}</span>
                      </div>
                      {partnershipCount > 1 && <span className="plus-count">+{partnershipCount - 1}</span>}
                    </div>
                    <span className="info-text">Partners Available</span>
                  </>
                );
              } else {
                return (
                  <span className="info-text">
                    {idea.looking_for && idea.looking_for.length > 0
                      ? idea.looking_for.map((item) => (typeof item === 'string' ? item : String(item))).join(', ')
                      : 'Coordinator, Partners, Freelancers'}
                  </span>
                );
              }
            })()}
          </div>
        </div> */}
        {/* <div className="idea-stats">
          <div className="stat-item">
            <SvgIcon icon="icon-message" />
            <span>{idea.comments_count || 0}</span>
          </div>
          {idea.ideas_count !== undefined && idea.ideas_count > 0 && (
            <div className="stat-item">
              <SvgIcon icon="icon-ideas" />
              <span>{idea.ideas_count}</span>
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
};
